import API from '../api/APIClient';
import {
  INCLUDE_PERSONAL_ANNOUNCEMENTS,
  getPrivacyProfile
} from '../config/privacy';
import { DAY_METADATA, MONTH_METADATA, ZL_CAUSE_METADATA, ZL_SUFFIX_METADATA } from '../constants/metadata';
import { areArraysIntersect, compareObjects } from '../utils/collections';
import {
  addDays,
  createDateRange,
  getGregorianDay,
  getJerusalemParts,
  isIsraelDst,
  jerusalemDateTimeToTimestamp,
  toJerusalemDateKey
} from '../utils/date';
import { numberToGimatria } from '../utils/gematria';
import {
  cleanHebrewToken,
  normalizeApiName,
  normalizeUserMonth,
  parseHebrewDateDisplay
} from '../utils/hebrewDate';
import { calculateTfilaOffset, resolveStatus, sortScheduleByTime } from '../utils/schedule';
import { hourFromCompactTime, minuteFromCompactTime } from '../utils/time';
import {
  buildInfoScreenPlatformFilter,
  contentPlatformValues,
  isInfoScreenSchedule
} from '../utils/contentPlatforms';
import { getSharedCalendarDays, mapSharedSchedule, mapSharedZmanim } from './calendarService';
import { getShabbatPlanning, buildShabbatPlanningMessage } from './shabbatService';
import { loadHolidaysRows, loadZmanimRows } from './csvService';
import { getHebcalItemsByYear, getJewishDatesForRange } from './hebcalService';
import {
  findCachedResource,
  getCachedResource,
  loadResource,
  storeResource
} from './resourceCache';
import { getDailyLearning } from './sefariaService';

const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const CRITICAL_MAX_AGE_MS = 55 * 1000;
const DAILY_MAX_AGE_MS = DAY_MS;
const RANGE_DAYS = 8;
const ACTIVE_STATUS_CODES = ['תלמיד', 'אברך', 'חייל', 'רב', 'צוות'];

const CLASS_FIELDS = ['crfd1_classid', 'crfd1_year'];
const MESSAGE_FIELDS = [
  'crfd1_title',
  'crfd1_content',
  'crfd1_duration',
  'crfd1_priority',
  'crfd1_hoursstart',
  'crfd1_minutesstart',
  'crfd1_hoursend',
  'crfd1_minutesend',
  'crfd1_weekday',
  'crfd1_platform',
  'crfd1_startson',
  'crfd1_endson'
];
const CHAIR_LIFTING_POLICY_FIELDS = [
  'crfd1_chairliftingpolicyid',
  'crfd1_name',
  'crfd1_isactive',
  'crfd1_seatlocations',
  'crfd1_seatlocation',
  'crfd1_weekdays',
  'crfd1_displayfromtime',
  'crfd1_displaytotime',
  'crfd1_notificationtime'
];
export const CHAIR_LIFTING_LOCATIONS = [
  { value: 648970000, key: 'front', label: 'בחלק הקדמי', color: '#6FC040' },
  { value: 648970001, key: 'back', label: 'בחלק האחורי', color: '#F2C94C' },
  { value: 648970002, key: 'tribune-right', label: 'בטריבונה ימין', color: '#51A7F9' },
  { value: 648970003, key: 'tribune-left', label: 'בטריבונה שמאל', color: '#D778E8' }
];
const CHAIR_LIFTING_MESSAGE_PATTERN = /הרמת\s+כי?סאות|כי?סאות/;
const USER_FIELDS = [
  'fullname',
  '_crfd1_class_value',
  'crfd1_birthday_day',
  'crfd1_birthday_month',
  'crfd1_status'
];
const MEMORIAL_FIELDS = [
  'crfd1_name',
  'crfd1_suffix',
  'crfd1_cause',
  'crfd1_day',
  'crfd1_month',
  'crfd1_year',
  'crfd1_boger',
  'crfd1_gender',
  'crfd1_note'
];
const TFILA_FIELDS = [
  'crfd1_title',
  'crfd1_note',
  'crfd1_location',
  'crfd1_duration',
  'crfd1_offset',
  'crfd1_relativeto',
  'crfd1_hours',
  'crfd1_minutes',
  'crfd1_rounddirection',
  'crfd1_roundminutes',
  'crfd1_hoursmin',
  'crfd1_minutesmin',
  'crfd1_hoursmax',
  'crfd1_minutesmax',
  'crfd1_daysinadvance',
  'crfd1_weekday',
  'crfd1_day',
  'crfd1_month',
  'crfd1_dst'
];
const SHIUR_FIELDS = [
  'crfd1_title',
  'crfd1_subtitle',
  'crfd1_location',
  'crfd1_hours',
  'crfd1_minutes',
  'crfd1_duration',
  'crfd1_daysinadvance',
  'crfd1_weekday',
  'crfd1_platform'
];
const SHIUR_FIELDS_WITHOUT_DURATION = SHIUR_FIELDS.filter(
  (field) => field !== 'crfd1_duration'
);
const MISSING_SHIUR_DURATION_PATTERN = /Could not find a property named ['"]crfd1_duration['"]/i;

function readOptionLabel(entity, fieldName) {
  return entity?.[`${fieldName}@OData.Community.Display.V1.FormattedValue`] || '';
}

function resolveUserBirthdayDay(user) {
  const formattedDay = cleanHebrewToken(readOptionLabel(user, 'crfd1_birthday_day'));
  if (formattedDay) {
    return formattedDay;
  }

  const rawDay = Number(user?.crfd1_birthday_day);
  if (!Number.isFinite(rawDay)) {
    return '';
  }

  return cleanHebrewToken(
    DAY_METADATA[rawDay - 648970000] ||
      DAY_METADATA[rawDay - 648969999] ||
      DAY_METADATA[rawDay - 1] ||
      ''
  );
}

function resolveUserBirthdayMonth(user) {
  const formattedMonth = normalizeUserMonth(readOptionLabel(user, 'crfd1_birthday_month'));
  if (formattedMonth) {
    return formattedMonth;
  }

  const rawMonth = Number(user?.crfd1_birthday_month);
  if (!Number.isFinite(rawMonth)) {
    return '';
  }

  return normalizeUserMonth(
    MONTH_METADATA[rawMonth - 648970000] ||
      MONTH_METADATA[rawMonth - 648969999] ||
      MONTH_METADATA[rawMonth - 1] ||
      ''
  );
}

function isActiveBirthdayUser(user) {
  const statusLabel = readOptionLabel(user, 'crfd1_status').trim();
  return !statusLabel || ACTIVE_STATUS_CODES.some((activeCode) => statusLabel.includes(activeCode));
}

function buildContainValuesFilter(fieldName, value) {
  return `Microsoft.Dynamics.CRM.ContainValues(PropertyName='${fieldName}',PropertyValues=%5B'${value}'%5D)`;
}

function addSelect(route, fields) {
  return `${route}${route.includes('?') ? '&' : '?'}$select=${fields.join(',')}`;
}

function cachedResult(key, maxAgeMs, fallback, alternate) {
  const cached = getCachedResource(key) || alternate;
  if (!cached) {
    return {
      data: typeof fallback === 'function' ? fallback() : fallback,
      updatedAt: null,
      stale: true,
      source: 'fallback',
      error: null
    };
  }

  return {
    data: cached.data,
    updatedAt: cached.updatedAt,
    stale: Date.now() - cached.updatedAt >= maxAgeMs,
    source: 'cache',
    error: null
  };
}

function skippedResource(data = []) {
  return {
    data,
    updatedAt: null,
    stale: false,
    source: 'privacy-filter',
    error: null
  };
}

async function loadWithAlternate(options, alternate) {
  if (!getCachedResource(options.key) && alternate) {
    storeResource(options.key, alternate.data, alternate.updatedAt);
  }
  return loadResource(options);
}

function mapZmanimRow(zmanimRows, dayRecord) {
  const row = zmanimRows.find(
    (item) => item.month === String(dayRecord.month) && item.day === String(dayRecord.dayOfMonth)
  );

  if (!row) {
    return {};
  }

  const { month, day, ...zmanim } = row;
  return zmanim;
}

export function mapHebcalEvent(event) {
  if (!event || event.category !== 'hebdate') {
    return event;
  }

  const parts = event.heDateParts;
  if (parts?.d && parts?.m && parts?.y) {
    return { ...event, hebrew: `${parts.d} ${parts.m} ${parts.y}` };
  }

  const yearSuffix = parseInt(event.title_orig?.slice(-4), 10);
  if (Number.isNaN(yearSuffix) || !event.hebrew) return event;
  const yearText = numberToGimatria(yearSuffix);
  const tokens = event.hebrew.trim().split(/\s+/);
  // Rosh Hashanah already includes the year upstream. Also repair older cached
  // rows with repeated years, including mixed Hebrew/ASCII quotation marks.
  while (tokens.length && cleanHebrewToken(tokens.at(-1)) === cleanHebrewToken(yearText)) {
    tokens.pop();
  }
  return { ...event, hebrew: [...tokens, yearText].join(' ') };
}

function buildEventsForDay(day, hebcalItems) {
  return hebcalItems
    .filter((item) => {
      const comparisonDate = addDays(
        day.dateKey,
        item.subcat === 'major' && (day.jewish?.m === 3 || day.jewish?.m === 4) ? -1 : 0
      );
      return comparisonDate === toJerusalemDateKey(item.date);
    })
    .map(mapHebcalEvent);
}

function shouldIncludeBirthday(hebrewDateTokens, user) {
  const day = resolveUserBirthdayDay(user);
  const month = resolveUserBirthdayMonth(user);
  const displayMonth = normalizeUserMonth(hebrewDateTokens[1]);

  if (!day || !month || day !== hebrewDateTokens[0]) {
    return false;
  }

  return (
    (hebrewDateTokens.length === 3 &&
      (month === displayMonth ||
        (displayMonth === 'אדר' && month.split(' ')[0] === 'אדר'))) ||
    (hebrewDateTokens[2] === 'א' && month === 'אדר א') ||
    (hebrewDateTokens[2] === 'ב' && (month === 'אדר' || month === 'אדר ב'))
  );
}

function matchesZlDate(hebrewDateTokens, item) {
  if (!hebrewDateTokens.length) {
    return false;
  }

  const dayText = cleanHebrewToken(DAY_METADATA[parseInt(item.crfd1_day, 10) - 648970000] || '');
  const monthText = cleanHebrewToken(MONTH_METADATA[parseInt(item.crfd1_month, 10) - 648970000] || '');

  if (dayText !== hebrewDateTokens[0]) {
    return false;
  }

  return (
    (hebrewDateTokens.length === 3 &&
      ((hebrewDateTokens[1] !== 'אדר' && monthText === hebrewDateTokens[1]) ||
        (hebrewDateTokens[1] === 'אדר' && monthText.split(' ')[0] === 'אדר'))) ||
    (hebrewDateTokens[2] === 'ב' && monthText === 'אדר ב') ||
    (hebrewDateTokens[2] === 'א' && (monthText === 'אדר' || monthText === 'אדר א'))
  );
}

function createClassIndex(classes) {
  return new Map(classes.map((item, index) => [item.crfd1_classid, index + 1]));
}

function calcShiur(id, classIndex) {
  const index = classIndex.get(id);
  return index ? `שיעור ${numberToGimatria(index)}` : undefined;
}

function dateBoundaryTimestamp(value) {
  if (value == null || value === '') {
    return null;
  }

  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function clockBoundaryMinutes(hour, minute) {
  if (hour == null) {
    return null;
  }

  const parsedHour = Number(hour);
  const parsedMinute = Number(minute ?? 0);
  if (!Number.isFinite(parsedHour)) {
    return null;
  }

  return parsedHour * 60 + (Number.isFinite(parsedMinute) ? parsedMinute : 0);
}

export function isMessageEligible(message, now = Date.now()) {
  if (!isInfoScreenSchedule(message)) {
    return false;
  }

  const nowTimestamp = new Date(now).getTime();
  const startsAt = dateBoundaryTimestamp(message?.crfd1_startson);
  const endsAt = dateBoundaryTimestamp(message?.crfd1_endson);
  if (startsAt != null && nowTimestamp < startsAt) {
    return false;
  }
  if (endsAt != null && nowTimestamp >= endsAt) {
    return false;
  }

  const current = getJerusalemParts(now);
  const weekdays = contentPlatformValues(message?.crfd1_weekday);
  if (weekdays.length && !weekdays.includes(648970000 + current.weekday)) {
    return false;
  }

  const nowMinutes = current.hours * 60 + current.minutes;
  const startMinutes = clockBoundaryMinutes(
    message?.crfd1_hoursstart,
    message?.crfd1_minutesstart
  );
  const endMinutes = clockBoundaryMinutes(message?.crfd1_hoursend, message?.crfd1_minutesend);
  return (
    (startMinutes == null || startMinutes <= nowMinutes) &&
    (endMinutes == null || endMinutes > nowMinutes)
  );
}

function compareMessagePriority(a, b) {
  if (a.crfd1_priority == null && b.crfd1_priority == null) return 0;
  if (a.crfd1_priority == null) return 1;
  if (b.crfd1_priority == null) return -1;
  return b.crfd1_priority - a.crfd1_priority;
}

function isChairLiftingCustomMessage(message) {
  return CHAIR_LIFTING_MESSAGE_PATTERN.test(String(message?.crfd1_title || ''));
}

// Screen-only display hours; shared policy times continue to control app reminders.
function isChairPolicyVisible(policy, current) {
  const nowMinutes = current.hours * 60 + current.minutes;
  return contentPlatformValues(policy?.crfd1_weekdays).includes(648970000 + current.weekday)
    && nowMinutes >= 10 * 60 && nowMinutes < 14 * 60;
}

export function buildChairLiftingAutoMessage(
  eligibleMessages,
  chairPolicies,
  now = Date.now()
) {
  if (!Array.isArray(chairPolicies) || chairPolicies.length === 0) {
    return null;
  }

  const current = getJerusalemParts(now);
  const selectedLocationValues = new Set();

  chairPolicies
    .filter((policy) => policy?.crfd1_isactive === true)
    .filter((policy) => isChairPolicyVisible(policy, current))
    .forEach((policy) => {
      contentPlatformValues(
        policy?.crfd1_seatlocations ?? policy?.crfd1_seatlocation
      ).forEach((value) => {
        selectedLocationValues.add(value);
      });
    });

  const chairLocations = CHAIR_LIFTING_LOCATIONS.filter((location) =>
    selectedLocationValues.has(location.value)
  );
  if (!chairLocations.length) {
    return null;
  }

  const template = [...eligibleMessages]
    .filter(isChairLiftingCustomMessage)
    .sort(compareMessagePriority)[0];

  return {
    type: 'chair-lifting',
    generatedDateKey: current.dateKey,
    crfd1_title: 'הרמת כיסאות אוטומטית',
    crfd1_duration: 7,
    crfd1_priority: template?.crfd1_priority ?? 2,
    chairLocations
  };
}

export function buildDescription(days, holidays, isNight) {
  const description = [];
  const activeIndex = isNight ? 1 : 0;
  const activeDayRecord = days.at(activeIndex);
  const currentWeekday = activeDayRecord.weekday;

  const nextHolidayIndex = days.findIndex((day, index) => {
    const isNearby = isNight ? index > 0 && index <= 2 : index <= 1;
    return (
      isNearby &&
      areArraysIntersect(
        holidays.filter((holiday) => holiday.specialShabat !== '1'),
        day.events,
        (holiday, event) => holiday.apiName === normalizeApiName(event.hebrew)
      )
    );
  });

  if (nextHolidayIndex !== -1) {
    const holidayEvent = days[nextHolidayIndex].events.find((event) => event.category === 'holiday');
    const holiday = holidays.find((row) => row.apiName === normalizeApiName(holidayEvent?.hebrew || ''));

    if (holiday) {
      if (nextHolidayIndex === 0 || holiday.yaaleVeYavo === '1' || holiday.nachem === '1') {
        description.push(holiday.name.replace("א' ד", nextHolidayIndex === 0 ? "א' ד" : ''));
      }

      if (
        nextHolidayIndex === 1 + (isNight ? 1 : 0) &&
        (holiday.yaaleVeYavo === '1' || holiday.nachem === '1')
      ) {
        description[description.length - 1] = `ערב ${description[description.length - 1]}`;
      }
    }
  }

  if (currentWeekday === 5 || currentWeekday === 6) {
    const nextShabatIndex = days.findIndex((day, index) => {
      const inRange = isNight ? index > 0 && index <= 7 : index < 7;
      return inRange && day.events.some((event) => event.category === 'parashat');
    });

    if (nextShabatIndex !== -1) {
      const parasha = days[nextShabatIndex].events.find((event) => event.category === 'parashat');
      description.push(`שבת ${parasha.hebrew.replace('־', '-')}`);
      if (nextShabatIndex === 1 + (isNight ? 1 : 0)) {
        description[description.length - 1] = `ערב ${description[description.length - 1]}`;
      }
    }

    const specialShabatIndex = days.findIndex((day, index) => {
      const inRange = isNight ? index > 0 && index <= 7 : index < 7;
      return (
        inRange &&
        areArraysIntersect(
          holidays.filter((holiday) => holiday.specialShabat === '1'),
          day.events,
          (holiday, event) => holiday.apiName === event.hebrew
        )
      );
    });

    if (specialShabatIndex !== -1 && description.length) {
      const shabatEvent = days[specialShabatIndex].events.find((event) => event.subcat === 'shabbat');
      const specialName = holidays.find((holiday) => holiday.apiName === shabatEvent?.hebrew)?.name;
      if (specialName) {
        description[description.length - 1] = `${description[description.length - 1]} - ${specialName}`;
      }
    }
  }

  const roshChodesh = activeDayRecord.events.find((event) => event.category === 'roshchodesh');
  if (roshChodesh) {
    description.push(roshChodesh.hebrew);
  } else {
    const nextDay = days[isNight ? 2 : 1];
    const nextRoshChodesh = nextDay?.events.find((event) => event.category === 'roshchodesh');
    if (nextRoshChodesh) {
      description.push(`ערב ${nextRoshChodesh.hebrew}`);
    }
  }

  return description.sort((a, b) => a.indexOf('ערב ') - b.indexOf('ערב ')).join(', ');
}

export function buildExtras(days, holidays, isNight) {
  const extras = [];
  const currentDay = days[isNight ? 1 : 0];
  const currentWeekday = currentDay?.weekday;

  if (!currentDay?.jewish?.m || !currentDay?.jewish?.d) {
    return extras;
  }

  const hasYaaleVeYavoHoliday = areArraysIntersect(
    holidays.filter((holiday) => holiday.yaaleVeYavo === '1'),
    currentDay.events,
    (holiday, event) => normalizeApiName(event.hebrew) === holiday.apiName
  );

  if (!hasYaaleVeYavoHoliday) {
    const nextShabatIndex = days.findIndex((day, index) => {
      const inRange = isNight ? index > 0 && index <= 7 : index < 7;
      return inRange && day.events.some((event) => event.category === 'parashat');
    });

    if (nextShabatIndex !== -1) {
      extras.push(
        days[nextShabatIndex].events.find((event) => event.category === 'parashat').hebrew.replace('־', '-')
      );
    }

    const nextSpecialShabatIndex = days.findIndex((day, index) => {
      const inRange = isNight ? index > 0 && index <= 7 : index < 7;
      return (
        inRange &&
        areArraysIntersect(
          holidays.filter((holiday) => holiday.specialShabat === '1'),
          day.events,
          (holiday, event) => holiday.apiName === event.hebrew
        )
      );
    });

    if (nextSpecialShabatIndex !== -1 && extras.length) {
      const eventName = days[nextSpecialShabatIndex].events.find((event) => event.subcat === 'shabbat')?.hebrew;
      const specialName = holidays.find((holiday) => holiday.apiName === eventName)?.name;
      if (specialName) {
        extras[extras.length - 1] += ` - ${specialName}`;
      }
    }
  }

  if (currentDay.events.some((event) => event.category === 'mevarchim')) {
    extras.push('ברכת החודש');
  }

  if (
    currentDay.jewish.m === 14 ||
    (currentDay.jewish.m === 1 && currentDay.jewish.d >= 1 && currentDay.jewish.d <= 21)
  ) {
    extras.push("לדוד ה' אורי וישעי");
  }

  if (currentDay.jewish.m === 1 && currentDay.jewish.d >= 1 && currentDay.jewish.d <= 10) {
    extras.push('עשרת ימי תשובה');
  }

  if (
    areArraysIntersect(
      holidays.filter((holiday) => holiday.nachem === '1'),
      currentDay.events,
      (holiday, event) => event.hebrew === holiday.apiName
    )
  ) {
    extras.push('נחם (במנחה)');
  }

  if (
    areArraysIntersect(
      holidays.filter((holiday) => holiday.anenu === '1'),
      currentDay.events,
      (holiday, event) => event.hebrew === holiday.apiName
    )
  ) {
    extras.push('עננו (במנחה)');
  }

  if (currentDay.events.some((event) => event.category === 'roshchodesh') && currentWeekday === 6) {
    extras.push('אתה יצרת');
  }

  if (hasYaaleVeYavoHoliday || currentDay.events.some((event) => event.category === 'roshchodesh')) {
    extras.push('יעלה ויבוא');
  }

  if (
    areArraysIntersect(
      holidays.filter((holiday) => holiday.alHaNisim === '1'),
      currentDay.events,
      (holiday, event) => event.hebrew === holiday.apiName
    )
  ) {
    extras.push('על הניסים');
  }

  const dayIndex = currentDay.jewish.m * 100 + currentDay.jewish.d;
  if (dayIndex > 122 && dayIndex < 915) extras.push('משיב הרוח ומוריד הגשם');
  if (dayIndex < 122 || dayIndex > 915) extras.push('מוריד הטל');
  if (dayIndex === 122) extras.push('תפילת גשם');
  if (dayIndex === 915) extras.push('תפילת טל');
  extras.push(dayIndex >= 207 && dayIndex < 915 ? 'ותן טל ומטר' : 'ותן ברכה');

  if (dayIndex > 915 && dayIndex < 1106) {
    const omerDay =
      (Math.floor(dayIndex / 100) - 9) * 30 -
      (Math.floor(dayIndex / 100) === 11 ? 1 : 0) +
      (dayIndex % 100) -
      15;
    extras.push(`${omerDay} לעומר`);
  }

  return extras;
}

export function buildMessageFeed(
  rawMessages,
  users,
  zls,
  classes,
  hebrewDateTokens,
  now,
  includePersonalAnnouncements = INCLUDE_PERSONAL_ANNOUNCEMENTS,
  chairPolicies = [],
  shabbatPlanning = [],
  includeBirthdays = false
) {
  const eligibleMessages = [...rawMessages].filter((message) => isMessageEligible(message, now));
  const hasChairPolicySource = Array.isArray(chairPolicies) && chairPolicies.length > 0;
  const chairLiftingMessage = buildChairLiftingAutoMessage(
    eligibleMessages,
    chairPolicies,
    now
  );
  const messages = eligibleMessages
    .filter((message) => !hasChairPolicySource || !isChairLiftingCustomMessage(message))
    .map((item) => ({ ...item, type: 'message' }));

  if (chairLiftingMessage) {
    messages.push(chairLiftingMessage);
  }
  const shabbatMessage = buildShabbatPlanningMessage(shabbatPlanning, now);
  if (shabbatMessage) {
    messages.push(shabbatMessage);
  }
  messages.sort(compareMessagePriority);

  if (!includePersonalAnnouncements && !includeBirthdays) {
    return messages;
  }

  const sortedClasses = [...classes].sort((a, b) => compareObjects(a, b, ['crfd1_year'], ['DESC']));
  const classIndex = createClassIndex(sortedClasses);

  const birthdays = users
    .filter(isActiveBirthdayUser)
    .filter((user) => shouldIncludeBirthday(hebrewDateTokens, user))
    .map((user) => ({
      fullname: user.fullname,
      'crfd1_status@OData.Community.Display.V1.FormattedValue': readOptionLabel(user, 'crfd1_status'),
      shiur: calcShiur(user._crfd1_class_value, classIndex),
      type: 'birthday'
    }));

  const memorials = (includePersonalAnnouncements ? zls : [])
    .filter((item) => matchesZlDate(hebrewDateTokens, item))
    .map((item) => ({ ...item, type: 'zl' }));

  return messages.concat(birthdays, memorials);
}

export function buildMessagesQuery(weekday) {
  const filters = [
    'statecode eq 0',
    `(crfd1_weekday eq null or ${buildContainValuesFilter(
      'crfd1_weekday',
      648970000 + weekday
    )})`,
    buildInfoScreenPlatformFilter()
  ];
  const route = `/crfd1_messages?$filter=${filters.join(' and ')}&$orderby=crfd1_priority`;
  return addSelect(route, MESSAGE_FIELDS);
}

export function buildChairLiftingPoliciesQuery(fields = CHAIR_LIFTING_POLICY_FIELDS) {
  return addSelect(
    '/crfd1_chairliftingpolicies?$filter=statecode eq 0&$orderby=crfd1_name',
    fields
  );
}

async function fetchChairLiftingPolicies({ signal }) {
  const fieldVariants = [
    CHAIR_LIFTING_POLICY_FIELDS,
    CHAIR_LIFTING_POLICY_FIELDS.filter((field) =>
      field !== 'crfd1_displayfromtime' && field !== 'crfd1_displaytotime'
    ),
    CHAIR_LIFTING_POLICY_FIELDS.filter((field) => field !== 'crfd1_seatlocations'),
    CHAIR_LIFTING_POLICY_FIELDS.filter((field) =>
      field !== 'crfd1_seatlocations' &&
      field !== 'crfd1_displayfromtime' &&
      field !== 'crfd1_displaytotime'
    )
  ];
  let firstError;
  for (const fields of fieldVariants) {
    try {
      return await API.select(buildChairLiftingPoliciesQuery(fields), 'list', { signal });
    } catch (error) {
      firstError = firstError || error;
    }
  }
  throw firstError;
}

export function buildTfilaQuery(day, dayIndex, enforceDaysInAdvance) {
  const { jewish } = day;
  const filters = ['statecode eq 0', 'crfd1_operation eq true'];
  if (enforceDaysInAdvance) filters.push(`crfd1_daysinadvance ge ${dayIndex}`);
  filters.push(
    `(crfd1_weekday eq null or ${buildContainValuesFilter('crfd1_weekday', 648970000 + day.weekday)})`
  );
  filters.push(
    `(crfd1_day eq null or ${buildContainValuesFilter('crfd1_day', 648969999 + jewish.d)})`
  );
  filters.push(
    `(crfd1_month eq null or ${buildContainValuesFilter('crfd1_month', 648969999 + jewish.m)})`
  );
  filters.push(
    `(crfd1_dst eq null or crfd1_dst eq ${isIsraelDst(day.dateKey) ? 648970001 : 648970000})`
  );
  return addSelect(`/crfd1_tfilas?$filter=${filters.join(' and ')}`, TFILA_FIELDS);
}

export function buildShiurQuery(
  day,
  dayIndex,
  enforceDaysInAdvance,
  includeDuration = true
) {
  const filters = ['statecode eq 0'];
  if (enforceDaysInAdvance) filters.push(`crfd1_daysinadvance ge ${dayIndex}`);
  filters.push(
    `(crfd1_weekday eq null or ${buildContainValuesFilter('crfd1_weekday', 648970000 + day.weekday)})`
  );
  filters.push(buildInfoScreenPlatformFilter());
  return addSelect(
    `/crfd1_shiurs?$filter=${filters.join(' and ')}`,
    includeDuration ? SHIUR_FIELDS : SHIUR_FIELDS_WITHOUT_DURATION
  );
}

function mapTfila(day, tfila, now) {
  const { hours, minutes } = calculateTfilaOffset(
    tfila,
    day.zmanim,
    day.dateKey,
    !day.zmanimResolved
  );
  const startsAt = jerusalemDateTimeToTimestamp(day.dateKey, hours, minutes);
  const endsAt = startsAt + (tfila.crfd1_duration ?? 15) * MINUTE_MS;
  return {
    ...tfila,
    hours,
    minutes,
    startsAt,
    endsAt,
    crfd1_subtitle: tfila.crfd1_note,
    type: 'tfila',
    status: resolveStatus(startsAt, endsAt, 10, now)
  };
}

function mapShiur(day, shiur, now) {
  const startsAt = jerusalemDateTimeToTimestamp(
    day.dateKey,
    shiur.crfd1_hours,
    shiur.crfd1_minutes
  );
  const endsAt = startsAt + (shiur.crfd1_duration ?? 30) * MINUTE_MS;
  return {
    ...shiur,
    hours: shiur.crfd1_hours,
    minutes: shiur.crfd1_minutes,
    startsAt,
    endsAt,
    type: 'shiur',
    status: resolveStatus(startsAt, endsAt, 5, now)
  };
}

async function fetchShiurs(day, dayIndex, enforceDaysInAdvance, signal) {
  try {
    return await API.select(
      buildShiurQuery(day, dayIndex, enforceDaysInAdvance),
      'list',
      { signal }
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (!MISSING_SHIUR_DURATION_PATTERN.test(message)) {
      throw error;
    }

    return API.select(
      buildShiurQuery(day, dayIndex, enforceDaysInAdvance, false),
      'list',
      { signal }
    );
  }
}

async function getScheduleForDay(day, dayIndex, options, context) {
  if (!day.jewish?.d || !day.jewish?.m) {
    return [];
  }

  const tfilaQuery = buildTfilaQuery(day, dayIndex, options.enforceTfilaAdvance);
  const [tfilasResult, shiursResult] = await Promise.all([
    loadResource({
      key: `tfilas:${day.dateKey}:${options.enforceTfilaAdvance ? 1 : 0}`,
      maxAgeMs: CRITICAL_MAX_AGE_MS,
      signal: context.signal,
      force: context.forceCritical,
      fallback: [],
      fetcher: ({ signal }) => API.select(tfilaQuery, 'list', { signal })
    }),
    loadResource({
      key: `shiurs:${day.dateKey}:${options.enforceShiurAdvance ? 1 : 0}`,
      maxAgeMs: CRITICAL_MAX_AGE_MS,
      signal: context.signal,
      force: context.forceCritical,
      fallback: [],
      fetcher: ({ signal }) => fetchShiurs(
        day,
        dayIndex,
        options.enforceShiurAdvance,
        signal
      )
    })
  ]);

  context.resourceResults.push(tfilasResult, shiursResult);
  return sortScheduleByTime([
    ...tfilasResult.data.map((tfila) => mapTfila(day, tfila, context.now)),
    ...shiursResult.data
      .filter(isInfoScreenSchedule)
      .map((shiur) => mapShiur(day, shiur, context.now))
  ]);
}

function remapSharedSchedule(day, now) {
  return mapSharedSchedule(day).map((item) => ({
    ...item,
    status: resolveStatus(item.startsAt, item.endsAt, item.type === 'tfila' ? 10 : 5, now)
  }));
}

function mergeScheduleItems(primaryItems, fallbackItems) {
  const merged = [];
  const seen = new Set();

  [...primaryItems, ...fallbackItems].forEach((item) => {
    const key = [
      item.type,
      `${item.hours}:${item.minutes}`,
      item.crfd1_title,
      item.crfd1_subtitle || '',
      item.crfd1_location || ''
    ].join('-');
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    merged.push(item);
  });

  return sortScheduleByTime(merged);
}

export async function buildSchedules(days, context) {
  const scheduleDays = days.map((day) => ({ ...day, schedule: [] }));
  const first = scheduleDays[0];
  const day0Schedule = first.sharedCalendarDay
    ? remapSharedSchedule(first.sharedCalendarDay, context.now)
    : await getScheduleForDay(
        first,
        0,
        { enforceTfilaAdvance: false, enforceShiurAdvance: false },
        context
      );

  const todayHasRemainingSchedule = day0Schedule.some((item) => item.status !== 'past');
  scheduleDays[0].schedule = todayHasRemainingSchedule ? day0Schedule : [];

  await Promise.all(
    [1, 2, 3].map(async (dayIndex) => {
      const day = scheduleDays[dayIndex];
      const revealFullTomorrow = dayIndex === 1 && !todayHasRemainingSchedule;
      const sharedSchedule = day.sharedCalendarDay
        ? remapSharedSchedule(day.sharedCalendarDay, context.now)
        : [];
      const canResolveLocally = Boolean(day.jewish?.d && day.jewish?.m);
      // An override is authoritative, including intentionally empty days. Never
      // fill it with the regular timetable when revealing tomorrow after hours.
      const hasSpecialSchedule = day.sharedCalendarDay?.scheduleOverride === true
        || (Array.isArray(day.sharedCalendarDay?.scheduleOverrideKinds)
          && day.sharedCalendarDay.scheduleOverrideKinds.length > 0)
        || day.sharedCalendarDay?.schedule?.some((item) => item.isSpecial === true);
      const hasUnifiedSchedule = day.sharedCalendarDay?.scheduleSource === 'unified';

      if (day.sharedCalendarDay && (
        hasUnifiedSchedule || hasSpecialSchedule || !revealFullTomorrow || !canResolveLocally
      )) {
        day.schedule = sharedSchedule;
        return;
      }

      const localSchedule = await getScheduleForDay(
        day,
        dayIndex,
        {
          enforceTfilaAdvance: !revealFullTomorrow,
          enforceShiurAdvance: !revealFullTomorrow
        },
        context
      );

      day.schedule = revealFullTomorrow
        ? mergeScheduleItems(localSchedule, sharedSchedule)
        : localSchedule;
    })
  );

  return scheduleDays;
}

function buildDays(dateKeys, jewishRange, sharedCalendar, zmanimRows, hebcalByYear) {
  const sharedByDate = new Map((sharedCalendar.days || []).map((day) => [day.date, day]));
  const jewishByDate = new Map(
    (jewishRange.dateKeys || []).map((dateKey, index) => [dateKey, jewishRange.dates?.[index]])
  );

  return dateKeys.map((dateKey) => {
    const gregorian = getGregorianDay(dateKey);
    const sharedDay = sharedByDate.get(dateKey);
    const day = {
      dateKey,
      year: gregorian.year,
      month: gregorian.month,
      dayOfMonth: gregorian.day,
      weekday: gregorian.weekday,
      jewish: jewishByDate.get(dateKey) || sharedDay?.hebrewDate?.numeric || { d: null, m: null, y: null },
      zmanim: sharedDay ? mapSharedZmanim(sharedDay) : mapZmanimRow(zmanimRows, {
        month: gregorian.month,
        dayOfMonth: gregorian.day
      }),
      zmanimResolved: Boolean(sharedDay),
      sharedCalendarDay: sharedDay || null,
      events: [],
      schedule: []
    };

    day.events = buildEventsForDay(day, hebcalByYear[day.year] || []);
    return day;
  });
}

function resolveNightStatus(firstDay, now) {
  if (!firstDay?.zmanim?.sunset) {
    return false;
  }
  const { dateKey, zmanim, zmanimResolved } = firstDay;

  const sunsetMinutes =
    (hourFromCompactTime(zmanim.sunset) +
      (zmanimResolved ? 0 : isIsraelDst(dateKey) ? 1 : 0)) *
      60 +
    minuteFromCompactTime(zmanim.sunset);
  const current = getJerusalemParts(now);
  return current.dateKey === dateKey && current.hours * 60 + current.minutes > sunsetMinutes;
}

function alternateCalendar(dateKey) {
  return findCachedResource(
    'calendar:',
    (calendar) => Array.isArray(calendar?.days) && calendar.days.some((day) => day.date === dateKey)
  );
}

function alternateJewishRange(dateKey) {
  return findCachedResource(
    'jewish-dates:',
    (range) => Array.isArray(range?.dateKeys) && range.dateKeys.includes(dateKey)
  );
}

async function getCriticalResources(
  dateKey,
  weekday,
  { signal, forceCritical, showsMessagesPanel, now }
) {
  const calendarKey = `calendar:${dateKey}:${RANGE_DAYS}`;
  const messagesKey = `messages:${dateKey}`;
  const [calendar, messages, chairPolicies, shabbatPlanning] = await Promise.all([
    loadWithAlternate(
      {
        key: calendarKey,
        maxAgeMs: CRITICAL_MAX_AGE_MS,
        signal,
        force: forceCritical,
        fallback: { days: [] },
        fetcher: ({ signal }) => getSharedCalendarDays(dateKey, RANGE_DAYS, { signal })
      },
      alternateCalendar(dateKey)
    ),
    showsMessagesPanel
      ? loadResource({
          key: messagesKey,
          maxAgeMs: CRITICAL_MAX_AGE_MS,
          signal,
          force: forceCritical,
          fallback: [],
          fetcher: ({ signal }) => API.select(buildMessagesQuery(weekday), 'list', { signal })
        })
      : skippedResource(),
    showsMessagesPanel
      ? loadResource({
          key: `chair-policies:${dateKey}`,
          maxAgeMs: CRITICAL_MAX_AGE_MS,
          signal,
          force: forceCritical,
          fallback: [],
          fetcher: fetchChairLiftingPolicies
        })
      : skippedResource(),
    showsMessagesPanel
      ? loadResource({
          key: `shabbat-planning:${dateKey}`,
          maxAgeMs: CRITICAL_MAX_AGE_MS,
          signal,
          force: forceCritical,
          fallback: [],
          fetcher: ({ signal }) => getShabbatPlanning(dateKey, now, { signal })
        })
      : skippedResource()
  ]);

  return { calendar, messages, chairPolicies, shabbatPlanning };
}

function readSupplementalResources(dateKey, dateKeys, years, showsMessagesPanel = false) {
  const jewishKey = `jewish-dates:${dateKey}:${RANGE_DAYS}`;
  const jewishAlternate = alternateJewishRange(dateKey);
  const emptyJewishRange = { start: dateKey, dateKeys, dates: [] };
  const values = {
    jewish: cachedResult(jewishKey, DAILY_MAX_AGE_MS, emptyJewishRange, jewishAlternate),
    classes: showsMessagesPanel
      ? cachedResult(`birthday-classes:${dateKey}`, DAILY_MAX_AGE_MS, [], findCachedResource('birthday-classes:'))
      : skippedResource(),
    users: showsMessagesPanel
      ? cachedResult(`birthday-users:${dateKey}`, DAILY_MAX_AGE_MS, [], findCachedResource('birthday-users:'))
      : skippedResource(),
    zls: showsMessagesPanel && INCLUDE_PERSONAL_ANNOUNCEMENTS
      ? cachedResult(`zls:${dateKey}`, DAILY_MAX_AGE_MS, [], findCachedResource('zls:'))
      : skippedResource(),
    learn: cachedResult(`learning:${dateKey}`, DAILY_MAX_AGE_MS, []),
    hebcal: {}
  };

  years.forEach((year) => {
    values.hebcal[year] = cachedResult(`hebcal:${year}`, DAILY_MAX_AGE_MS, []);
  });
  return values;
}

async function loadSupplementalResources(dateKey, dateKeys, years, { signal, forceSupplemental, showsMessagesPanel = false }) {
  const jewishKey = `jewish-dates:${dateKey}:${RANGE_DAYS}`;
  const jewishAlternate = alternateJewishRange(dateKey);
  const common = { maxAgeMs: DAILY_MAX_AGE_MS, signal, force: forceSupplemental };
  const commonTasks = [
    loadWithAlternate(
      {
        ...common,
        key: jewishKey,
        fallback: { start: dateKey, dateKeys, dates: [] },
        fetcher: async ({ signal }) => ({
          start: dateKey,
          dateKeys,
          dates: await getJewishDatesForRange(dateKeys, { signal })
        })
      },
      jewishAlternate
    ),
    loadResource({
      ...common,
      key: `learning:${dateKey}`,
      fallback: [],
      fetcher: ({ signal }) => getDailyLearning(dateKey, { signal })
    }),
    ...years.map((year) =>
      loadResource({
        ...common,
        key: `hebcal:${year}`,
        fallback: [],
        fetcher: ({ signal }) => getHebcalItemsByYear(year, { signal })
      })
    )
  ];
  const personalTasks = showsMessagesPanel
    ? [
        loadWithAlternate(
          {
            ...common,
            key: `birthday-classes:${dateKey}`,
            fallback: [],
            fetcher: ({ signal }) =>
              API.select(encodeURIComponent(addSelect('/crfd1_classes', CLASS_FIELDS)), 'list', { signal })
          },
          findCachedResource('birthday-classes:')
        ),
        loadWithAlternate(
          {
            ...common,
            key: `birthday-users:${dateKey}`,
            fallback: [],
            fetcher: ({ signal }) =>
              API.select(
                encodeURIComponent(addSelect('/systemusers?$filter=crfd1_birthday_day ne null and crfd1_birthday_month ne null&$orderby=fullname', USER_FIELDS)),
                'list',
                { signal }
              )
          },
          findCachedResource('birthday-users:')
        ),
        INCLUDE_PERSONAL_ANNOUNCEMENTS ? loadWithAlternate(
          {
            ...common,
            key: `zls:${dateKey}`,
            fallback: [],
            fetcher: ({ signal }) =>
              API.select(
                addSelect(
                  '/crfd1_zls?$filter=statecode eq 0&$orderby=crfd1_year,crfd1_name',
                  MEMORIAL_FIELDS
                ),
                'list',
                { signal }
              )
          },
          findCachedResource('zls:')
        ) : skippedResource()
      ]
    : [skippedResource(), skippedResource(), skippedResource()];

  const [jewish, learn, ...hebcalResults] = await Promise.all(commonTasks);
  const [classes, users, zls] = await Promise.all(personalTasks);
  return {
    jewish,
    classes,
    users,
    zls,
    learn,
    hebcal: Object.fromEntries(years.map((year, index) => [year, hebcalResults[index]]))
  };
}

function flattenResourceResults(critical, supplemental, scheduleResults) {
  return [
    critical.calendar,
    critical.messages,
    critical.chairPolicies,
    critical.shabbatPlanning,
    supplemental.jewish,
    supplemental.classes,
    supplemental.users,
    supplemental.zls,
    supplemental.learn,
    ...Object.values(supplemental.hebcal),
    ...scheduleResults
  ];
}

function buildSyncState(results, includeSupplemental) {
  const failures = results.filter((result) => result?.error);
  const timestamps = results.map((result) => result?.updatedAt).filter(Boolean);
  return {
    stale: failures.length > 0 || results.some((result) => result?.stale),
    supplementalPending: !includeSupplemental,
    lastUpdated: timestamps.length ? Math.max(...timestamps) : null,
    failedResources: failures.length,
    errorMessages: [...new Set(failures.map((result) => result.error?.message || 'שגיאת רשת'))]
  };
}

export function createEmptyInfoScreenData(
  now = Date.now(),
  privacyProfile = getPrivacyProfile(false)
) {
  const dateKey = getJerusalemParts(now).dateKey;
  return {
    days: createDateRange(dateKey, RANGE_DAYS).map((key) => {
      const day = getGregorianDay(key);
      return {
        dateKey: key,
        year: day.year,
        month: day.month,
        dayOfMonth: day.day,
        weekday: day.weekday,
        jewish: { d: null, m: null, y: null },
        zmanim: {},
        zmanimResolved: false,
        sharedCalendarDay: null,
        events: [],
        schedule: []
      };
    }),
    zmanimContextDays: [],
    isNight: false,
    description: '',
    messages: [],
    learn: [],
    holidays: [],
    extras: [],
    metadata: {
      day: DAY_METADATA,
      month: MONTH_METADATA,
      zlCause: ZL_CAUSE_METADATA,
      zlSuffix: ZL_SUFFIX_METADATA
    },
    privacy: privacyProfile,
    sync: {
      stale: true,
      supplementalPending: true,
      lastUpdated: null,
      failedResources: 0,
      errorMessages: []
    }
  };
}

function finiteOr(value, fallback) {
  return Number.isFinite(Number(value)) ? Number(value) : fallback;
}

export function normalizeInfoScreenSnapshot(
  snapshot,
  now = Date.now(),
  privacyProfile = getPrivacyProfile(false)
) {
  if (!snapshot || !Array.isArray(snapshot.days) || snapshot.days.length === 0) {
    return null;
  }

  const baseline = createEmptyInfoScreenData(now, privacyProfile);
  const daysByDate = new Map(
    snapshot.days
      .filter((day) => day?.dateKey)
      .map((day) => [day.dateKey, day])
  );
  const days = baseline.days.map((fallbackDay, index) => {
    const day = daysByDate.get(fallbackDay.dateKey) || snapshot.days[index] || {};
    const legacyGregorian = day.gregorian || {};

    return {
      ...fallbackDay,
      ...day,
      dateKey: fallbackDay.dateKey,
      year: finiteOr(day.year ?? legacyGregorian.year, fallbackDay.year),
      month: finiteOr(day.month ?? legacyGregorian.month, fallbackDay.month),
      dayOfMonth: finiteOr(
        day.dayOfMonth ?? legacyGregorian.dayOfMonth ?? legacyGregorian.day,
        fallbackDay.dayOfMonth
      ),
      weekday: finiteOr(day.weekday ?? legacyGregorian.weekday, fallbackDay.weekday),
      jewish: day.jewish && typeof day.jewish === 'object' ? day.jewish : fallbackDay.jewish,
      zmanim: day.zmanim && typeof day.zmanim === 'object' ? day.zmanim : {},
      events: Array.isArray(day.events) ? day.events.map(mapHebcalEvent) : [],
      schedule: Array.isArray(day.schedule) ? day.schedule : []
    };
  });

  return {
    ...baseline,
    ...snapshot,
    days,
    zmanimContextDays:
      Array.isArray(snapshot.zmanimContextDays) && snapshot.zmanimContextDays.length
        ? snapshot.zmanimContextDays
        : days,
    messages: privacyProfile.showsMessagesPanel && Array.isArray(snapshot.messages)
      ? snapshot.messages : [],
    learn: Array.isArray(snapshot.learn) ? snapshot.learn : [],
    holidays: Array.isArray(snapshot.holidays) ? snapshot.holidays : [],
    extras: Array.isArray(snapshot.extras) ? snapshot.extras : [],
    metadata: { ...baseline.metadata, ...(snapshot.metadata || {}) },
    privacy: baseline.privacy,
    sync: { ...baseline.sync, ...(snapshot.sync || {}) }
  };
}

export function getCachedInfoScreenData(
  now = Date.now(),
  privacyProfile = getPrivacyProfile(false)
) {
  const dateKey = getJerusalemParts(now).dateKey;
  const cached = getCachedResource(`snapshot:messages-v4:${privacyProfile.mode}:${dateKey}`);
  const normalized = normalizeInfoScreenSnapshot(cached?.data, now, privacyProfile);
  if (!normalized) {
    return null;
  }

  return {
    ...normalized,
    sync: {
      ...normalized.sync,
      stale: true,
      supplementalPending: true,
      lastUpdated: cached.updatedAt
    }
  };
}

export async function getInfoScreenData({
  includeSupplemental = true,
  forceCritical = false,
  forceSupplemental = false,
  privacyProfile = getPrivacyProfile(false),
  signal,
  now = Date.now()
} = {}) {
  const current = getJerusalemParts(now);
  const previousDateKey = addDays(current.dateKey, -1);
  const dateKeys = createDateRange(current.dateKey, RANGE_DAYS);
  const years = [
    ...new Set([previousDateKey, ...dateKeys].map((dateKey) => getGregorianDay(dateKey).year))
  ];
  const [critical, supplemental, holidays, zmanimRows] = await Promise.all([
    getCriticalResources(current.dateKey, current.weekday, {
      signal,
      forceCritical,
      now,
      showsMessagesPanel: privacyProfile.showsMessagesPanel === true
    }),
    includeSupplemental
      ? loadSupplementalResources(current.dateKey, dateKeys, years, { signal, forceSupplemental, showsMessagesPanel: privacyProfile.showsMessagesPanel === true })
      : Promise.resolve(readSupplementalResources(current.dateKey, dateKeys, years, privacyProfile.showsMessagesPanel === true)),
    loadHolidaysRows(),
    loadZmanimRows()
  ]);

  const hebcalByYear = Object.fromEntries(
    Object.entries(supplemental.hebcal).map(([year, result]) => [year, result.data || []])
  );
  const days = buildDays(
    dateKeys,
    supplemental.jewish.data,
    critical.calendar.data,
    zmanimRows,
    hebcalByYear
  );
  const previousZmanimDay = buildDays(
    [previousDateKey],
    { dateKeys: [], dates: [] },
    { days: [] },
    zmanimRows,
    hebcalByYear
  )[0];
  const scheduleResults = [];
  const daysWithSchedule = await buildSchedules(days, {
    signal,
    forceCritical,
    now,
    resourceResults: scheduleResults
  });
  const isNight = resolveNightStatus(daysWithSchedule[0], now);
  const activeDay = daysWithSchedule[isNight ? 1 : 0];
  const hebrewDate = activeDay.events.find((event) => event.category === 'hebdate')?.hebrew || '';
  const hebrewDateTokens = parseHebrewDateDisplay(hebrewDate);
  const results = flattenResourceResults(critical, supplemental, scheduleResults);

  const data = {
    days: daysWithSchedule,
    zmanimContextDays: [previousZmanimDay, ...daysWithSchedule],
    isNight,
    description: buildDescription(daysWithSchedule, holidays, isNight),
    messages: buildMessageFeed(
      critical.messages.data || [],
      supplemental.users.data || [],
      supplemental.zls.data || [],
      supplemental.classes.data || [],
      hebrewDateTokens,
      now,
      INCLUDE_PERSONAL_ANNOUNCEMENTS,
      critical.chairPolicies.data || [],
      critical.shabbatPlanning.data || [],
      privacyProfile.showsMessagesPanel === true
    ),
    learn: supplemental.learn.data || [],
    holidays,
    extras: buildExtras(daysWithSchedule, holidays, isNight),
    metadata: {
      day: DAY_METADATA,
      month: MONTH_METADATA,
      zlCause: ZL_CAUSE_METADATA,
      zlSuffix: ZL_SUFFIX_METADATA
    },
    privacy: privacyProfile,
    sync: buildSyncState(results, includeSupplemental)
  };

  if (results.some((result) => result?.updatedAt)) {
    storeResource(`snapshot:messages-v4:${privacyProfile.mode}:${current.dateKey}`, data);
  }

  return data;
}
